import * as PIXI from "pixi.js";
import { Symbol } from "src/Symbol";
import { WinCounter } from "src/WinCounter";
import { delay, moveObjectToNewParent } from "src/shared/util/UtilFunctions";
import { SpinResponse, Win } from "src/shared/util/Types";
import { WinText } from "src/WinText";
import { IReel, Reel, ReelState } from "./Reel";
import { CascadeCounter } from "src/CascadeCounter";
import { GameEngine } from "src/server/GameEngine";
import { GameAssets } from "src/shared/util/GameAssets";
import { GameState, GlobalState } from "src/shared/util/GlobalState";
import { Character } from "./Character";
import { Multiplier } from "src/bonus/Multiplier";
import { soundManager } from "src/shared/UI/SoundManager";
import { MultiplierParticle } from "src/MultiplierParticles";
import { Spine } from "@pixi/spine-pixi";
import gsap from "gsap";
import AppSingleton from "src/shared/util/AppSingleton";
import { backgroundSize } from "src/scenes/GameView";

const STOP_REEL_INTERVAL: number = 50;
const SPIN_REEL_INTERVAL: number = 20;
// Define an enum for the states
export enum PanelState {
  IDLE = "IDLE",
  SPIN = "SPIN",
  SPIN_END = "SPIN_END",
  MATCH = "MATCH",
  CASCADE = "CASCADE",
  WIN = "WIN",
  STOPPING = "STOPPING",
  MULTIPLIER = "MULTIPLIER",
  SCATTER_MATCH = "SCATTER_MATCH",
  STOP = "STOP"
}

export class Panel extends PIXI.Container {
  private panelData!: string[][];
  private reels: IReel[];
  private winSymbols: Symbol[];
  private winLayer: PIXI.Graphics;
  private topContainer: PIXI.Container;
  private bottomContainer: PIXI.Container;
  private currentState: PanelState = PanelState.IDLE;
  private winCounter!: WinCounter;
  private cascadeCounter!: CascadeCounter;
  private multiplier!: Multiplier;
  private content: PIXI.Container;
  private currentReelIndex: number = 0;
  private isQuickStop: boolean = false;
  private data!: SpinResponse;
  private panelMaskTexture!: PIXI.Sprite;
  private panelBack!: PIXI.Sprite;
  private logo!: Spine;
  private character: Character;
  private cloudsLeft: Spine;
  private cloudsRight: Spine;
  private cloudsMask: PIXI.Graphics;
  private stopNextId!: NodeJS.Timeout | null;
  // This variables serves to provide a precise location from which particle will be fired
  private doveParticlePosition: { x: number, y: number } = { x: 0, y: 0 };
  private doveParticleShotMultipliers: Set<Symbol> = new Set();

  constructor() {
    super();

    this.reels = [];
    this.winSymbols = [];
    this.bottomContainer = new PIXI.Container();
    this.topContainer = new PIXI.Container();
    this.content = new PIXI.Container();
    this.addChild(this.content);

    this.panelBack = new PIXI.Sprite(GameAssets.textures.gameAtlas.textures["panelMain.webp"]);
    this.panelBack.anchor.set(0.5);
    this.panelBack.position.set(0, 0);

    this.panelMaskTexture = new PIXI.Sprite(GameAssets.textures.gameAtlas.textures["panelMask.webp"]);
    this.panelMaskTexture.anchor.set(0.5);
    this.panelMaskTexture.scale.set(1, 0.98);
    this.panelMaskTexture.position.set(0, Symbol.SYMBOL_HEIGHT * GameEngine.getInstance().config.rows / 2 - 5);
    this.panelMaskTexture.position.set(0, -10);

    this.cloudsMask = new PIXI.Graphics();

    this.cloudsLeft = Spine.from({
      skeleton: 'cloudsFrontJson',
      atlas: 'cloudsFrontAtlas',
    });

    this.cloudsRight = Spine.from({
      skeleton: 'cloudsFrontJson',
      atlas: 'cloudsFrontAtlas',
    });

    this.cloudsLeft.skeleton.setSkinByName("main");
    this.cloudsLeft.state.setAnimation(0, "idle", true);
    this.cloudsLeft.state.timeScale = 0.5;
    this.cloudsLeft.setSize(1920, 1080);
    this.cloudsLeft.mask = this.cloudsMask;
    this.cloudsLeft.x = 200;

    this.cloudsRight.skeleton.setSkinByName("main");
    this.cloudsRight.state.setAnimation(0, "idle", true);
    this.cloudsRight.state.timeScale = 0.7;
    this.cloudsRight.setSize(-1920, 1080);
    this.cloudsRight.mask = this.cloudsMask;
    this.cloudsRight.x = -200;


    this.logo = Spine.from({ skeleton: "gameLogoJson", atlas: "gameLogoAtlas" });

    this.logo.skeleton.setSkinByName("default");
    this.logo.state.setAnimation(0, "title_idle", true);

    // this.logo.pivot.set(0.5);
    this.logo.scale.set(0.1);
    this.logo.position.set(0, -400);
    this.logo.roundPixels = true;

    this.bottomContainer.mask = this.panelMaskTexture;

    this.winLayer = new PIXI.Graphics().rect(-1000, -1000,
      2000, 2000).fill(0);
    this.winLayer.alpha = 0.7;

    this.bottomContainer.y = -290;
    this.topContainer.y = this.bottomContainer.y;
    this.winLayer.visible = false;
    this.winLayer.zIndex = 50;

    this.bottomContainer.addChild(this.winLayer);

    this.winCounter = new WinCounter();

    this.cascadeCounter = new CascadeCounter();
    this.cascadeCounter.position.set(0, -400);
    this.cascadeCounter.visible = false;

    this.multiplier = new Multiplier();
    this.multiplier.visible = false;

    this._changeState(PanelState.IDLE);

    this.character = new Character();

    this.content.addChild(this.character);
    this.content.addChild(this.cloudsLeft);
    this.content.addChild(this.cloudsRight);
    this.content.addChild(this.cloudsMask);
    this.content.addChild(this.panelBack);
    this.content.addChild(this.bottomContainer);
    this.content.addChild(this.panelMaskTexture);
    this.content.addChild(this.logo);
    this.content.addChild(this.topContainer);
    this.content.addChild(this.winCounter);
    this.content.addChild(this.cascadeCounter);
    this.content.addChild(this.multiplier);
  }

  public init(panelData: string[][]) {
    this.panelData = panelData;
    this._createReels();
  }

  public getState(): PanelState {
    return this.currentState;
  }

  public async setData(data: SpinResponse) {
    console.log("new spin response");
    console.log(data);
    console.log("panel state is: " + this.currentState);
    if (this.currentState === PanelState.CASCADE && data.cascade) {
      const hasHighValueSymbol = data.cascade.some(cascade =>
        parseInt(cascade.symbol) >= 100
      );
      if (hasHighValueSymbol) {
        this.character.showDoves();
      }

      this.data = data;
      this.panelData = JSON.parse(JSON.stringify(data.panel));
      this._onCascade().then(() => {
        this._changeState(PanelState.STOP);
      });
    } else {
      const hasHighValueSymbol = data.panelNew.some(reel =>
        reel.some(symbol => parseInt(symbol) >= 100)
      );
      if (hasHighValueSymbol) {
        await this.character.showDoves();
      }

      this.data = data;

      this.multiplier.setMultiplier(this.data.multiplier || 1);
      this.panelData = JSON.parse(JSON.stringify(data.panelNew));
      this.reels.forEach((reel, index) => {
        const reelData = this.panelData[index];
        reel.setData(reelData);
      });

      this._stopReels();
      this._moveMultiplierParticles();
    };
  }

  private _moveMultiplierParticles(moveDuration: number = 0.4): void {
    moveDuration = GlobalState.getInstance().fastOn ? 0.2 : 0.4;

    const newPanelData: string[][] = JSON.parse(JSON.stringify(this.data.panelNew));

    for (let reelId = 0; reelId < GameEngine.getInstance().config.reels; ++reelId) {
      const reelPositionX: number = this.reels[reelId].position.x;
      const newReelData = newPanelData[reelId];

      for (let symbolId = 0; symbolId < GameEngine.getInstance().config.rows; ++symbolId) {
        // If isn't a multiplier symbol, skip
        if (!(parseInt(newReelData[symbolId]) >= 100)) {
          continue;
        }

        if (this.doveParticleShotMultipliers.has(this.reels[reelId].getSymbol(symbolId)
        )) {
          continue;
        }
        this.doveParticleShotMultipliers.add(this.reels[reelId].getSymbol(symbolId));

        const particle: MultiplierParticle = new MultiplierParticle({
          x: this.doveParticlePosition.x,
          y: this.doveParticlePosition.y,
        });

        particle.sprites.forEach(sprite => {
          this.content.addChild(sprite);
        });

        particle.moveTo({
          x: reelPositionX,
          y: (this.reels[reelId] as Reel)._getY(symbolId) + this.bottomContainer.y,
        }, moveDuration, () => {
          // On complete
          particle.stopAndClear();
        });
      }
    }
  }

  private _stopReels() {
    if (this.panelData.length && this.currentState === PanelState.SPIN_END) {
      this._changeState(PanelState.STOPPING);
    }
  }

  private _createReels() {
    for (let i = 0; i < GameEngine.getInstance().config.reels; i++) {
      const reel = new Reel(this.panelData[i], i);
      reel.x = i * Symbol.SYMBOL_WIDTH - GameEngine.getInstance().config.reels * Symbol.SYMBOL_WIDTH / 2 + Symbol.SYMBOL_WIDTH / 2;
      this.bottomContainer.addChild(reel);
      this.reels.push(reel);
      reel.on('land', this._onReelLand, this);
      reel.on(ReelState.IDLE, this._onReelStop, this);
      reel.on(ReelState.SPIN_END, this._onReelSpinEnd, this);
    }
  }

  private _onReelStop(reel: Reel) {
    if (reel.getIndex() === GameEngine.getInstance().config.reels - 1) {
      setTimeout(() => {
        this._changeState(PanelState.STOP);
      }, 100)
    }
  }

  private _onReelSpinEnd(reel: Reel) {
    if (reel.getIndex() === GameEngine.getInstance().config.reels - 1) {
      //Workaround. Use promises instead of reel index
      setTimeout(() => {
        this._changeState(PanelState.SPIN_END);
        this._stopReels();
      }, 100)
    }
  }

  private _onReelLand(reel: Reel) {
    // moveObjectToNewParent(reel, this.topContainer);
  }

  // Method to change the state and execute the corresponding actions
  private _changeState(newState: PanelState) {
    if (this.currentState === newState) {
      return; // No change if the same state
    }

    // Enter logic for the new state
    this.currentState = newState;
    this.emit(this.currentState);
    switch (this.currentState) {
      case PanelState.IDLE:
        this._onEnterIdle();
        break;
      case PanelState.SPIN:
        (async () => { this._onEnterSpin(); })();
        break;
      case PanelState.WIN:
        this._onRoundWin().then(() => {
          this._changeState(PanelState.SCATTER_MATCH);
        });
        break;
      case PanelState.MATCH:
        const currentAnim = this.logo.state.getCurrent(0)?.animation?.name;
        if (currentAnim !== 'title_hide' && currentAnim !== 'idle' && this.data.roundWin > 0) {
          this.logo.state.setAnimation(0, "title_hide", false);
          this.logo.state.addAnimation(0, "idle", true);
        }

        this._onMatch().then(() => {
          this._resetQuickStop();
          // Only cascade if there are actual movements
          if (this.data.movements && this.data.movements.length > 0) {
            this._changeState(PanelState.CASCADE);

          } else {
            // Skip to multiplier phase
            this._changeState(PanelState.MULTIPLIER);
          }
        });
        break;
      case PanelState.SCATTER_MATCH:
        if ((this.data.state === GameState.MAIN_TO_BONUS || this.data.retriggerFreespins) && this.data.scatters) {
          this._matchScatters(this.data.scatters).then(() => {
            this._changeState(PanelState.IDLE);

          })
        } else {
          this._changeState(PanelState.IDLE);
        }
        break;
      case PanelState.MULTIPLIER:
        if (this.data.roundWin) {
          (async () => { await soundManager.playSound("multiplierApply"); })();

          this._matchMultipliers().then(() => {
            this.cascadeCounter.resetMultiplier();
            this.cascadeCounter.visible = false;

            const currentAnim = this.logo.state.getCurrent(0)?.animation?.name;
            if (currentAnim !== 'title_idle' && currentAnim !== 'title_show') {
              this.logo.state.setAnimation(0, "title_show", false);
              this.logo.state.addAnimation(0, "title_idle", true);
            }

            this._changeState(PanelState.WIN);
          });
        } else {
          this._changeState(PanelState.SCATTER_MATCH);
        }
        break;
      case PanelState.STOPPING:
        this._onStopping();
        break;
      case PanelState.STOP:
        if (this.data.wins.length || this.data.scatterWins) {
          this.cascadeCounter.visible = true;
          this.cascadeCounter.setWin(this.data.roundWin);

          this._changeState(PanelState.MATCH);
        } else {
          this._changeState(PanelState.MULTIPLIER);
        }

        break;
    }
  }

  // Define custom logic for entering and exiting each state
  private _onEnterIdle() {
    this.cascadeCounter.visible = false;

    this.doveParticleShotMultipliers.clear();
  }

  private async _onEnterSpin() {
    await soundManager.playSound("reelSpin");

    this._resetQuickStop();
    this.currentReelIndex = 0;
    this.winSymbols.length = 0;
    this.panelData.length = 0;

    for (let i = 0; i < this.reels.length; i++) {
      this.reels[i].resetData();
      moveObjectToNewParent(this.reels[i], this.bottomContainer);
    }

    this.reels[this.currentReelIndex].triggerSpin();
    this.currentReelIndex++;
    this._startNextSpin();
  }

  private _startNextSpin() {
    this.stopNextId = setTimeout(() => {
      if (this.reels[this.currentReelIndex]) {
        this.reels[this.currentReelIndex].triggerSpin();
        this.currentReelIndex++;
        this._startNextSpin();
      }
    }, SPIN_REEL_INTERVAL);
  }

  private async _onRoundWin() {
    this._resetQuickStop();
    await this.winCounter.showPrize(this.data.roundWin);
  }

  private async _onMatch(): Promise<void> {
    await soundManager.playSound("matchSymbols", {
      complete: async () => {
        await soundManager.playSound("matchSymbolsOff");
      }
    });

    // Wait for all _showWin promises in parallel
    await Promise.all(this.data.wins.map((winData) => this._showWin(winData)));
    if (this.data.scatterWins) {
      await Promise.all(this.data.scatterWins.map((winData) => this._showScatterWin(winData)));
    }

    // Wait for _onMovements to resolve
    await this._onMovements();
  }

  private async _matchMultipliers(): Promise<void> {
    let symbols: Symbol[] = [];
    for (let i = 0; i < this.reels.length; i++) {
      for (let j = 0; j < GameEngine.getInstance().config.rows; j++) {
        if (this.reels[i].getSymbol(j).getType() === '100') {
          symbols.push(this.reels[i].getSymbol(j));
        }
      }
    }

    for (const symbol of symbols) {
      // Get the global position of the symbol
      const globalPosition = symbol.toGlobal(new PIXI.Point(0, 0));
      // Convert to local position relative to this.content
      const localPosition = this.content.toLocal(globalPosition);

      const multText = new PIXI.BitmapText({
        text: `x${symbol.getMultiplier()}`,
        style: {
          fontSize: 40,
          fontFamily: "multiplierFont",
        }
      });
      multText.anchor.set(0.5);
      multText.zIndex = 1;
      multText.position.set(localPosition.x, localPosition.y);
      this.content.addChild(multText);

      // Get cascade counter position for animation target
      const cascadeGlobalPos = this.cascadeCounter.toGlobal(new PIXI.Point(0, 0));
      const cascadeLocalPos = this.content.toLocal(cascadeGlobalPos);

      // Create timeline
      const tl = gsap.timeline({
        onComplete: () => {
          this.content.removeChild(multText);
          this.cascadeCounter.addMultiplier(symbol.getMultiplier());
          multText.destroy();
        }
      });

      // Fade in and scale up
      tl.to(multText, {
        alpha: 1,
        duration: 0.2,
        ease: "power2.out"
      })
        .to(multText.scale, {
          x: 2,
          y: 2,
          duration: 0.2,
          ease: "power2.out"
        }, 0) // Start at the same time as alpha animation
        // Move to cascade counter while maintaining scale and opacity
        .to(multText, {
          x: cascadeLocalPos.x,
          y: cascadeLocalPos.y,
          duration: 0.4,
          ease: "power2.inOut"
        }, "-=0.1") // Start slightly before the previous animation ends
        // Fade out and scale down
        .to(multText, {
          alpha: 0,
          duration: 0.2,
          ease: "power2.in"
        }, "-=0.1")
        .to(multText.scale, {
          x: 1,
          y: 1,
          duration: 0.2,
          ease: "power2.in"
        }, "-=0.2"); // Start at the same time as alpha fade out

      if (this.isQuickStop) {
        symbol.win(this.isQuickStop)
      } else {
        await symbol.win(this.isQuickStop);
      }
    }
  }

  private _resetQuickStop() {
    this.isQuickStop = false;
    this.emit('resetQuickStop');
  }

  private async _matchScatters(winData: Win): Promise<void> {
    this.winLayer.visible = true;
    const winPromises = winData.coordinates.map(async (coordinates) => {
      const symbol: Symbol = this.reels[coordinates.reel].getSymbol(coordinates.row);
      moveObjectToNewParent(symbol, this.topContainer);
      await symbol.win(this.isQuickStop);
      moveObjectToNewParent(symbol, this.bottomContainer);
    });

    await Promise.all(winPromises); // Wait for all win() calls to complete
    this.winLayer.visible = false;
  }

  public async _onCascade() {
    const promises: Promise<void>[] = [];
    if (!this.data.cascade) {
      return
    }

    for (let i = 0; i < this.data.cascade.length; i++) {
      const data = this.data.cascade[i];
      const symbol: Symbol = Symbol.pool.acquire();
      symbol.y = -800;
      moveObjectToNewParent(this.reels[data.from.reel], this.bottomContainer);
      this.reels[data.from.reel].addChild(symbol);
      symbol.setType(data.symbol);

      const promise = this.reels[data.from.reel].moveSymbol(symbol, data.to.row, this.isQuickStop ? 0 : i * 0.05);

      promises.push(promise);
    }
    this._moveMultiplierParticles();
    await Promise.all(promises);
  }

  private async _onMovements() {
    if (!this.data.movements) {
      return
    }
    const movePromises = this.data.movements.map(async (data, index) => {
      const symbol = this.reels[data.from.reel].getSymbol(data.from.row);
      moveObjectToNewParent(this.reels[data.from.reel], this.bottomContainer);
      await this.reels[data.from.reel].moveSymbol(symbol, data.to.row, this.isQuickStop ? 0 : index * 0.05);
      // moveObjectToNewParent(this.reels[data.from.reel], this.topContainer);
    });

    await Promise.all(movePromises);
  }

  private async _showWin(winData: Win) {
    this.winLayer.visible = true;
    const winPromises = winData.coordinates.map(async (coordinates) => {
      const symbol: Symbol = this.reels[coordinates.reel].getSymbol(coordinates.row);
      moveObjectToNewParent(symbol, this.topContainer);
      await this.reels[coordinates.reel].removeSymbol(coordinates.row, this.isQuickStop);
    });

    // Sort the coordinates array by reel index
    const sortedCoordinates = [...winData.coordinates].sort((a, b) => a.reel - b.reel);

    // Find the middle reel index
    const middleIndex = Math.floor(sortedCoordinates.length / 2);

    // Get the coordinates for the middle reel
    const middleCoordinates = sortedCoordinates[middleIndex];

    const symbol: Symbol = this.reels[middleCoordinates.reel].getSymbol(middleCoordinates.row);
    // Get WinText from pool, set position, and show win amount
    const winText = WinText.pool.acquireWinText();
    winText.position.set(this.reels[middleCoordinates.reel].x, symbol.y);
    winText.showWin(winData.win);
    this.topContainer.addChild(winText); // Add WinText to the display list

    await Promise.all(winPromises); // Wait for all win() calls to complete
    this.winLayer.visible = false;
  }

  private async _showScatterWin(winData: Win) {
    this.winLayer.visible = true;
    const winPromises = winData.coordinates.map(async (coordinates) => {
      const symbol: Symbol = this.reels[coordinates.reel].getSymbol(coordinates.row);
      moveObjectToNewParent(symbol, this.topContainer);

      if (this.data.state === GameState.BONUS) {
        await this.reels[coordinates.reel].removeSymbol(coordinates.row, this.isQuickStop);
      } else if (this.data.state === GameState.MAIN_TO_BONUS) {
        // Call win() on the moved symbol, not the reel's symbol
        await symbol.win(this.isQuickStop);
        // Move it back to bottomContainer after animation
        moveObjectToNewParent(symbol, this.bottomContainer);
      }
    });

    // Sort the coordinates array by reel index
    const sortedCoordinates = [...winData.coordinates].sort((a, b) => a.reel - b.reel);

    // Find the middle reel index
    const middleIndex = Math.floor(sortedCoordinates.length / 2);

    // Get the coordinates for the middle reel
    const middleCoordinates = sortedCoordinates[middleIndex];

    const symbol: Symbol = this.reels[middleCoordinates.reel].getSymbol(middleCoordinates.row);
    // Get WinText from pool, set position, and show win amount
    const winText = WinText.pool.acquireWinText();
    winText.position.set(this.reels[middleCoordinates.reel].x, symbol.y);
    winText.showWin(winData.win);
    winText.zIndex = 10;
    this.topContainer.addChild(winText); // Add WinText to the display list

    await Promise.all(winPromises); // Wait for all win() calls to complete
    this.winLayer.visible = false;
  }

  private async _onStopping() {
    this.currentReelIndex = 0;
    if (this.stopNextId) {
      clearTimeout(this.stopNextId);
    }
    if (this.isQuickStop) {
      for (let i = this.currentReelIndex; i < this.reels.length; i++) {
        this.reels[i].triggerStop(this.isQuickStop);
      }
    } else {
      this._stopNext();
    }
  }

  private _stopNext() {
    if (this.isQuickStop) {
      if (this.stopNextId) {
        clearTimeout(this.stopNextId);
        this.stopNextId = null;
        for (let i = this.currentReelIndex; i < this.reels.length; i++) {
          this.reels[i].triggerStop(this.isQuickStop);
        }
      }
    } else {
      this.stopNextId = null;
      this.reels[this.currentReelIndex].triggerStop(this.isQuickStop);
      this.currentReelIndex++;

      if (this.reels[this.currentReelIndex]) {
        this.stopNextId = setTimeout(async () => {
          this._stopNext();
          if (this.currentReelIndex === 4) {
            await soundManager.playSound("reelLand");
          }
        }, STOP_REEL_INTERVAL);
      }
    }
  }

  public setGameState(gameState: GameState) {
    switch (gameState) {
      case GameState.MAIN:
        this.character.setSkin('main');
        this.panelBack.texture = GameAssets.textures.gameAtlas.textures["panelMain.webp"];
        this.multiplier.visible = false;

        this.cloudsLeft.skeleton.setSkinByName("main");
        this.cloudsRight.skeleton.setSkinByName("main");
        break;
      case GameState.BONUS:
        this.character.setSkin('fs');
        this.panelBack.texture = GameAssets.textures.gameAtlas.textures["panelFs.webp"];
        this.multiplier.visible = true;
        this.multiplier.setMultiplier(1);

        this.cloudsLeft.skeleton.setSkinByName("fs");
        this.cloudsRight.skeleton.setSkinByName("fs");
        break;
    }
  }

  public onPortrait(data?: any) {
    this.y = -30;
    const maxRatio = 0.62;
    const maxScale = 1.4;
    const ratio = data.ratio;

    let scale: number;
    if (ratio >= maxRatio) {
      scale = maxScale;
    } else {
      // Linear interpolation between minScale and maxScale
      scale = ratio / maxRatio * maxScale;
      this.y = -30 + ((1 - (ratio / maxRatio)) * -100);
    }
    this.scale.set(scale);

    this.character.scale.set(0.25);
    this.character.position.set(150, -70);
    this.doveParticlePosition = { x: 150, y: -600 };
    this.multiplier.position.set(-250, -550);

    this.cloudsMask.clear();
    this.cloudsMask.rect(
      -backgroundSize.x / 2,
      -backgroundSize.y / 2,
      backgroundSize.x,
      backgroundSize.y
    ).fill(0xffffff);

    this.cloudsLeft.position.set(200.0, 0.0);
    this.cloudsRight.position.set(-200.0, 0.0);
  }

  public onLandscape(data?: any) {
    this.scale.set(1);
    this.position.set(0, 0);
    this.character.position.set(600, 400);
    this.character.scale.set(0.3);
    this.doveParticlePosition = { x: 670, y: -300 };
    this.multiplier.position.set(-420, -430);

    this.position.set(0, 0);

    this.cloudsMask.clear();
    this.cloudsMask.rect(
      -backgroundSize.x / 2,
      -backgroundSize.y / 2,
      backgroundSize.x,
      backgroundSize.y
    ).fill(0xffffff);

    this.cloudsLeft.position.set(200.0, 0.0);
    this.cloudsRight.position.set(-200.0, 0.0);

    // const debug = new PIXI.Graphics();
    // debug.beginFill("0xFF0000");
    // debug.drawCircle(670, -300, 50);
    // debug.endFill();
    // this.content.addChild(debug);
  }

  public spin() {
    this._changeState(PanelState.SPIN);
  }

  public async quickStop() {
    this.isQuickStop = true;
    switch (this.currentState) {
      case PanelState.SPIN:
        this.reels.forEach((reel) => {
          reel.triggerStop(this.isQuickStop);
        })
        break;
      case PanelState.SPIN_END:
        this._stopReels();
        break;
      case PanelState.STOPPING:
        this._onStopping();
        break;
      case PanelState.MATCH:
        break;
      case PanelState.WIN:
        this.winCounter.quickStop();
        break;
    }
  }
}
